// Roles allowed to use the admin ledger.
//
// IMPORTANT: the integration spec says "ROLE_SUPER_ADMIN", but the real login
// response returns "ROLE_ADMIN_SUPER" (the words SUPER and ADMIN are swapped).
// We accept both spellings so the gate works regardless. Confirm the canonical
// string with backend and trim this list once you know.
export const LEDGER_ALLOWED_ROLES = [
  "ROLE_SUPER_ADMIN", // per the integration spec doc
  "ROLE_ADMIN_SUPER", // actual string returned by /auth login
  "ROLE_ADMIN_OWNER",
];

export const MOCK_LEDGER_ROLES = ["ROLE_ADMIN_SUPER", "ROLE_ADMIN_OWNER"];

// TEMPORARY dev switch. Set to false once real roles populate redux.
export const USE_MOCK_LEDGER_ROLES = true;

export const LEDGER_ENVIRONMENTS = {
  LIVE: "LIVE",
  SANDBOX: "SANDBOX",
};

// Standard double-entry classifications (spec §3.1 "account type cheat sheet").
// Drives the natural balance side and report grouping. Immutable after create,
// so the form forces a deliberate choice. `hint` is surfaced under the dropdown.
export const ACCOUNT_TYPE_OPTIONS = [
  { label: "Asset", value: "ASSET", hint: "What we own — bank, cash, receivables" },
  {
    label: "Liability",
    value: "LIABILITY",
    hint: "What we owe — payables, VAT, customer balances",
  },
  { label: "Equity", value: "EQUITY", hint: "Owner stake — share capital, retained earnings" },
  { label: "Income", value: "INCOME", hint: "Money earned — fee/interest income" },
  { label: "Expense", value: "EXPENSE", hint: "Money spent — commission paid, infra" },
  {
    label: "Interbank",
    value: "INTERBANK",
    hint: "Bridge / clearing — NIBSS in/out flows",
  },
];

// TODO: align with backend's accepted ISO currency codes.
export const CURRENCY_OPTIONS = [
  { label: "NGN", value: "NGN" },
  { label: "USD", value: "USD" },
  { label: "GHS", value: "GHS" },
  { label: "KES", value: "KES" },
];

// Only real values — the "All Statuses" entry is rendered by CustomSelectInput's
// built-in placeholder (value="all"). Adding an empty-value option here collides
// with displayEmpty and knocks the floating label out of position.
export const LEDGER_STATUS_OPTIONS = [
  { label: "Active", value: "ACTIVE" },
  { label: "Inactive", value: "INACTIVE" },
];

// Special operational role attached to a GL (backend `role` column). Most
// accounts have none; a handful are wired to system functions. The Create
// Account dialog auto-fills this from the GL catalog — it is never free-typed.
export const GL_ROLE_LABELS = {
  MERCHANT_PAYABLE: "Merchant Payable",
  PAYOUT_IN_TRANSIT: "Payout in Transit",
  FEE_INCOME: "Fee Income",
  BANK: "Bank",
};

// Predefined chart of accounts. Selecting a ledger name in the Create Account
// dialog locks in its account number, type, currency and role so admins can't
// mistype them. `role: null` = no special operational role. Keep this in sync
// with the backend Flyway seed data.
export const GL_ACCOUNT_CATALOG = [
  { name: "DEPOSIT BANK MIRROR ACCOUNT", accountNumber: "10080001", accountType: "ASSET", currency: "NGN", role: "BANK" },
  { name: "Cash in bank", accountNumber: "10080002", accountType: "ASSET", currency: "NGN", role: null },
  { name: "Cash in ZenithBank", accountNumber: "10080003", accountType: "ASSET", currency: "NGN", role: null },
  { name: "MERCHANT SERVICES SETTLEMENT", accountNumber: "10430003", accountType: "ASSET", currency: "NGN", role: null },
  { name: "VAT", accountNumber: "20070003", accountType: "LIABILITY", currency: "NGN", role: null },
  { name: "UNCLEARED EFFECTS", accountNumber: "20100001", accountType: "LIABILITY", currency: "NGN", role: "PAYOUT_IN_TRANSIT" },
  { name: "MERCHANT SERVICES PAYABLE GL", accountNumber: "20180005", accountType: "LIABILITY", currency: "NGN", role: "MERCHANT_PAYABLE" },
  { name: "WayaQuick merchant deposit Account", accountNumber: "29999102", accountType: "LIABILITY", currency: "NGN", role: null },
  { name: "WayaQuick merchant PayOut wallet Account", accountNumber: "29999103", accountType: "LIABILITY", currency: "NGN", role: null },
  { name: "share capital", accountNumber: "30030001", accountType: "EQUITY", currency: "NGN", role: null },
  { name: "Transfer fee income", accountNumber: "40060018", accountType: "INCOME", currency: "NGN", role: null },
  { name: "Transaction Fees", accountNumber: "40060039", accountType: "INCOME", currency: "NGN", role: "FEE_INCOME" },
  { name: "Cost of Sales", accountNumber: "50050019", accountType: "EXPENSE", currency: "NGN", role: null },
  { name: "Nibss Outflow - Bank", accountNumber: "88100001", accountType: "INTERBANK", currency: "NGN", role: null },
  { name: "NIBSS Inflow - Bank", accountNumber: "99100001", accountType: "INTERBANK", currency: "NGN", role: null },
];

// Dropdown options for the GL picker (value = account number, shown by name).
export const GL_ACCOUNT_OPTIONS = GL_ACCOUNT_CATALOG.map((gl) => ({
  value: gl.accountNumber,
  label: gl.name,
}));
